import React, { useState, useEffect } from "react";
import {
  Dialog,
  TextField,
  Box,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { modalStyles } from "components/Modal/modalStyles";
import { countiresList } from "data/countries";
import { connect } from "react-redux";
import { createUserDetailsAction } from "Actions/createUserAction";

const STEPS = [
  "User Details",
  "Feature Config",
  "Application Config",
  "3rd Party Config",
  "Billing Details",
  "Confirmation",
  "Status",
];

const ProductType = ["Standard", "Premium"];
const BillingDuration = ["Monthly", "Quarterly", "Annually"];
const Players = ["Kaltura", "Native"];
const PaymentGateways = ["Amazon", "Stripe", "Custom"];

function AddTenantModal({ open, onClose, createUserDetailsAction, usersData, message, defaultUserMessage }) {
  const [activeStep, setActiveStep] = useState(0);
  const [userDetails, setUserDetails] = useState({});
  const [featureConfig, setFeatureConfig] = useState({
    sara: false,
    saraInsights: false,
    saraSearch: false,
    watched: 30,
    byw_watched: 30,
    watch_live: 5,
    sia: false,
    personalization: false,
    aiCompanion: false,
    gamification: false,
    kalturaRE: false,
    pip: false,
    downloadToGo: false,
    player: null,
    paymentGateway: {},
  });
  const [confirmStatus, setConfirmStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setActiveStep(0);
    setUserDetails({});
    setFeatureConfig({
      sara: false, saraInsights: false, saraSearch: false,
      watched: 30, byw_watched: 30, watch_live: 5,
      sia: false, personalization: false, aiCompanion: false,
      gamification: false, kalturaRE: false, pip: false, downloadToGo: false,
      player: null, paymentGateway: {},
    });
    setConfirmStatus(false);
    setErrorMessage("");
    setLoading(false);
    onClose();
  };

  const validateStep0 = () => {
    const mail = /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/;
    if (!userDetails.customerName) return "Customer Name is required";
    if (!userDetails.legalEntityName) return "Legal Entity Name is required";
    if (!userDetails.legalEntityAddress) return "Legal Entity Address is required";
    if (!userDetails.country) return "Country is required";
    if (!userDetails.pincode) return "Pincode is required";
    if (!userDetails.contactName) return "Contact Name is required";
    if (!userDetails.contactEmail || !mail.test(userDetails.contactEmail)) return "Valid email is required";
    if (!userDetails.contactPhoneNumber) return "Phone Number is required";
    return "";
  };

  const validateStep4 = () => {
    if (!userDetails.productType) return "Product Type is required";
    if (!userDetails.billingContactName) return "Billing Contact Name is required";
    if (!userDetails.billingContactEmail) return "Billing Email is required";
    if (!userDetails.billingContactCountry) return "Billing Country is required";
    if (!userDetails.billingContactAddress) return "Billing Address is required";
    if (!userDetails.billingPincode) return "Billing Pincode is required";
    if (!userDetails.billingContactPhone) return "Billing Phone is required";
    if (!userDetails.billingDuration) return "Billing Duration is required";
    return "";
  };

  const handleNext = () => {
    setErrorMessage("");
    if (activeStep === 0) {
      const err = validateStep0();
      if (err) { setErrorMessage(err); return; }
    } else if (activeStep === 4) {
      const err = validateStep4();
      if (err) { setErrorMessage(err); return; }
    } else if (activeStep === 5) {
      if (!confirmStatus) { setErrorMessage("Please confirm details before proceeding"); return; }
      // Submit
      createUserDetailsAction({ userDetails, featureConfig });
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setErrorMessage("");
    setActiveStep((prev) => prev - 1);
  };

  // ─── Render Steps ───────────────────────────────────────────────────

  const renderUserDetails = () => (
    <MDBox display="flex" flexDirection="column" gap={2}>
      <TextField size="small" label="Customer Name *" value={userDetails.customerName || ""}
        onChange={(e) => setUserDetails({ ...userDetails, customerName: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Legal Entity Name *" value={userDetails.legalEntityName || ""}
        onChange={(e) => setUserDetails({ ...userDetails, legalEntityName: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Legal Entity Address *" value={userDetails.legalEntityAddress || ""}
        onChange={(e) => setUserDetails({ ...userDetails, legalEntityAddress: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <Autocomplete size="small" options={countiresList.map((c) => c.name)}
        value={userDetails.country || null}
        onChange={(e, value) => {
          const c = countiresList.find((x) => x.name === value);
          setUserDetails({ ...userDetails, country: value, countryCode: c?.dialCode || "" });
        }}
        renderInput={(params) => <TextField {...params} label="Country *" sx={modalStyles.input} />} />
      <TextField size="small" label="Pincode *" value={userDetails.pincode || ""}
        onChange={(e) => setUserDetails({ ...userDetails, pincode: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Contact Name *" value={userDetails.contactName || ""}
        onChange={(e) => setUserDetails({ ...userDetails, contactName: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Contact Email *" value={userDetails.contactEmail || ""}
        onChange={(e) => setUserDetails({ ...userDetails, contactEmail: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Contact Phone *" value={userDetails.contactPhoneNumber || ""}
        onChange={(e) => setUserDetails({ ...userDetails, contactPhoneNumber: e.target.value })}
        sx={modalStyles.input} fullWidth
        InputProps={{ startAdornment: userDetails.countryCode ? <InputAdornment position="start">{userDetails.countryCode}</InputAdornment> : null }} />
    </MDBox>
  );

  const renderFeatureConfig = () => (
    <MDBox>
      <MDTypography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Features</MDTypography>
      <MDBox display="flex" flexWrap="wrap" gap={0}>
        {[
          { name: "sara", label: "Sara" },
          { name: "saraInsights", label: "Sara Insights" },
          { name: "saraSearch", label: "Sara Search" },
          { name: "sia", label: "Sia" },
          { name: "aiCompanion", label: "AI Companion" },
          { name: "gamification", label: "Gamification" },
          { name: "personalization", label: "Personalization" },
          { name: "kalturaRE", label: "Kaltura Rec Engine" },
        ].map((f) => (
          <FormControlLabel key={f.name} sx={{ width: "48%" }}
            control={<Checkbox size="small" checked={!!featureConfig[f.name]}
              onChange={(e) => setFeatureConfig({ ...featureConfig, [f.name]: e.target.checked })} />}
            label={<Typography sx={{ fontSize: 13 }}>{f.label}</Typography>} />
        ))}
      </MDBox>
      <MDTypography sx={{ fontSize: 13, fontWeight: 600, mt: 2, mb: 1 }}>Application Control</MDTypography>
      <MDBox display="flex" gap={2} alignItems="center" mb={1}>
        <Typography sx={{ fontSize: 13, minWidth: 140 }}>Watch Event</Typography>
        <TextField size="small" type="number" value={featureConfig.watched}
          onChange={(e) => { const v = parseInt(e.target.value) || 0; if (v >= 0 && v <= 100) setFeatureConfig({ ...featureConfig, watched: v }); }}
          sx={{ width: 80, ...modalStyles.input }} InputProps={{ inputProps: { min: 0, max: 100 } }} />
        <Typography sx={{ fontSize: 12, color: "#5D6D7E" }}>%</Typography>
      </MDBox>
      <MDBox display="flex" gap={2} alignItems="center" mb={1}>
        <Typography sx={{ fontSize: 13, minWidth: 140 }}>BYW Event</Typography>
        <TextField size="small" type="number" value={featureConfig.byw_watched}
          onChange={(e) => { const v = parseInt(e.target.value) || 0; if (v >= 0 && v <= 100) setFeatureConfig({ ...featureConfig, byw_watched: v }); }}
          sx={{ width: 80, ...modalStyles.input }} InputProps={{ inputProps: { min: 0, max: 100 } }} />
        <Typography sx={{ fontSize: 12, color: "#5D6D7E" }}>%</Typography>
      </MDBox>
      <MDBox display="flex" gap={2} alignItems="center">
        <Typography sx={{ fontSize: 13, minWidth: 140 }}>Live Event</Typography>
        <TextField size="small" type="number" value={featureConfig.watch_live}
          onChange={(e) => { const v = parseInt(e.target.value) || 0; if (v >= 0) setFeatureConfig({ ...featureConfig, watch_live: v }); }}
          sx={{ width: 80, ...modalStyles.input }} InputProps={{ inputProps: { min: 0 } }} />
        <Typography sx={{ fontSize: 12, color: "#5D6D7E" }}>min</Typography>
      </MDBox>
    </MDBox>
  );

  const renderAppConfig = () => (
    <MDBox>
      <MDTypography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Application Config</MDTypography>
      <MDBox display="flex" flexWrap="wrap" gap={0}>
        {[
          { name: "pip", label: "PIP" },
          { name: "downloadToGo", label: "Download To Go" },
        ].map((f) => (
          <FormControlLabel key={f.name} sx={{ width: "48%" }}
            control={<Checkbox size="small" checked={!!featureConfig[f.name]}
              onChange={(e) => setFeatureConfig({ ...featureConfig, [f.name]: e.target.checked })} />}
            label={<Typography sx={{ fontSize: 13 }}>{f.label}</Typography>} />
        ))}
      </MDBox>
    </MDBox>
  );

  const renderThirdPartyConfig = () => (
    <MDBox display="flex" flexDirection="column" gap={2}>
      <MDTypography sx={{ fontSize: 13, fontWeight: 600 }}>Third Party Config</MDTypography>
      <Autocomplete size="small" options={Players} value={featureConfig.player || null}
        onChange={(e, value) => setFeatureConfig({ ...featureConfig, player: value })}
        renderInput={(params) => <TextField {...params} label="Player" sx={modalStyles.input} />} />
      <Autocomplete size="small" options={PaymentGateways}
        value={featureConfig.paymentGateway?.type || featureConfig.paymentGateway?.name || null}
        onChange={(e, value) => setFeatureConfig({
          ...featureConfig,
          paymentGateway: value === "Custom" ? { type: "Custom" } : { name: value },
        })}
        renderInput={(params) => <TextField {...params} label="Payment Gateway" sx={modalStyles.input} />} />
      {featureConfig.paymentGateway?.type === "Custom" && (
        <>
          <TextField size="small" label="Gateway Name" sx={modalStyles.input} fullWidth
            value={featureConfig.paymentGateway?.name || ""}
            onChange={(e) => setFeatureConfig({ ...featureConfig, paymentGateway: { ...featureConfig.paymentGateway, name: e.target.value } })} />
          <TextField size="small" label="Gateway Value" sx={modalStyles.input} fullWidth
            value={featureConfig.paymentGateway?.value || ""}
            onChange={(e) => setFeatureConfig({ ...featureConfig, paymentGateway: { ...featureConfig.paymentGateway, value: e.target.value } })} />
        </>
      )}
    </MDBox>
  );

  const renderBillingDetails = () => (
    <MDBox display="flex" flexDirection="column" gap={2}>
      <Autocomplete size="small" options={ProductType} value={userDetails.productType || null}
        onChange={(e, value) => setUserDetails({ ...userDetails, productType: value })}
        renderInput={(params) => <TextField {...params} label="Product Type *" sx={modalStyles.input} />} />
      <TextField size="small" label="Billing Contact Name *" value={userDetails.billingContactName || ""}
        onChange={(e) => setUserDetails({ ...userDetails, billingContactName: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Billing Contact Email *" value={userDetails.billingContactEmail || ""}
        onChange={(e) => setUserDetails({ ...userDetails, billingContactEmail: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <Autocomplete size="small" options={countiresList.map((c) => c.name)}
        value={userDetails.billingContactCountry || null}
        onChange={(e, value) => {
          const c = countiresList.find((x) => x.name === value);
          setUserDetails({ ...userDetails, billingContactCountry: value, billingCountryCode: c?.dialCode || "" });
        }}
        renderInput={(params) => <TextField {...params} label="Billing Country *" sx={modalStyles.input} />} />
      <TextField size="small" label="Billing Address *" value={userDetails.billingContactAddress || ""}
        onChange={(e) => setUserDetails({ ...userDetails, billingContactAddress: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Billing Pincode *" value={userDetails.billingPincode || ""}
        onChange={(e) => setUserDetails({ ...userDetails, billingPincode: e.target.value })}
        sx={modalStyles.input} fullWidth />
      <TextField size="small" label="Billing Phone *" value={userDetails.billingContactPhone || ""}
        onChange={(e) => setUserDetails({ ...userDetails, billingContactPhone: e.target.value })}
        sx={modalStyles.input} fullWidth
        InputProps={{ startAdornment: userDetails.billingCountryCode ? <InputAdornment position="start">{userDetails.billingCountryCode}</InputAdornment> : null }} />
      <Autocomplete size="small" options={BillingDuration} value={userDetails.billingDuration || null}
        onChange={(e, value) => setUserDetails({ ...userDetails, billingDuration: value })}
        renderInput={(params) => <TextField {...params} label="Billing Duration *" sx={modalStyles.input} />} />
    </MDBox>
  );

  const renderConfirmation = () => (
    <MDBox>
      <MDTypography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>Review & Confirm</MDTypography>
      <MDBox sx={{ background: "#F8FAFC", borderRadius: 2, p: 2, mb: 2, fontSize: 13, lineHeight: 2 }}>
        <div><strong>Customer:</strong> {userDetails.customerName}</div>
        <div><strong>Legal Entity:</strong> {userDetails.legalEntityName}</div>
        <div><strong>Country:</strong> {userDetails.country}</div>
        <div><strong>Contact:</strong> {userDetails.contactName} ({userDetails.contactEmail})</div>
        <div><strong>Product:</strong> {userDetails.productType}</div>
        <div><strong>Billing Duration:</strong> {userDetails.billingDuration}</div>
      </MDBox>
      <FormControlLabel
        control={<Checkbox checked={confirmStatus} onChange={(e) => { setConfirmStatus(e.target.checked); setErrorMessage(""); }} />}
        label={<Typography sx={{ fontSize: 13 }}>I confirm customer details and billing details</Typography>}
      />
    </MDBox>
  );

  const renderStatus = () => (
    <MDBox textAlign="center" py={3}>
      {defaultUserMessage?.hzStatus === "200" ? (
        <MDBox>
          <MDTypography sx={{ fontSize: 16, fontWeight: 700, color: "#17A589", mb: 1 }}>
            ✓ Tenant Created Successfully
          </MDTypography>
          <MDTypography sx={{ fontSize: 13, color: "#5D6D7E" }}>
            Username and password have been sent to the contact email.
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox>
          <CircularProgress size={24} sx={{ mb: 2 }} />
          <MDTypography sx={{ fontSize: 13, color: "#5D6D7E" }}>
            Creating customer, database, and user...
          </MDTypography>
          {usersData && <MDTypography sx={{ fontSize: 12, color: "#17A589", mt: 1 }}>✓ Customer created</MDTypography>}
          {message?.tenantId && <MDTypography sx={{ fontSize: 12, color: "#17A589", mt: 0.5 }}>✓ Database created</MDTypography>}
        </MDBox>
      )}
    </MDBox>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0: return renderUserDetails();
      case 1: return renderFeatureConfig();
      case 2: return renderAppConfig();
      case 3: return renderThirdPartyConfig();
      case 4: return renderBillingDetails();
      case 5: return renderConfirmation();
      case 6: return renderStatus();
      default: return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { ...modalStyles.paperWide, maxWidth: 720 } }} maxWidth={false}>
      {/* Header */}
      <MDBox sx={modalStyles.header}>
        <MDBox>
          <MDTypography sx={modalStyles.headerTitle}>New Tenant</MDTypography>
          <MDTypography sx={modalStyles.headerSubtitle}>Create and provision a new customer tenant</MDTypography>
        </MDBox>
        <Box component="button" sx={modalStyles.closeButton} onClick={handleClose}>
          ✕
        </Box>
      </MDBox>

      {/* Stepper */}
      <MDBox sx={{ px: 3, pt: 2, pb: 1 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            background: "none !important",
            boxShadow: "none !important",
            padding: "16px 0 8px",
            "& .MuiStepConnector-root": {
              color: "#E8EDF2",
            },
            "& .MuiStepConnector-root.Mui-active": {
              color: "#2F36CB",
            },
            "& .MuiStepConnector-root.Mui-completed": {
              color: "#2F36CB",
            },
            "& .MuiStepConnector-line": {
              borderColor: "currentColor !important",
              borderTopWidth: "2px !important",
              opacity: "1 !important",
            },
            "& .MuiStepIcon-root": {
              background: "#E8EDF2 !important",
              fill: "#E8EDF2 !important",
              stroke: "#E8EDF2 !important",
              width: "12px !important",
              height: "12px !important",
              boxShadow: "none !important",
            },
            "& .MuiStepIcon-root.Mui-active": {
              background: "#2F36CB !important",
              fill: "#2F36CB !important",
              stroke: "#2F36CB !important",
            },
            "& .MuiStepIcon-root.Mui-completed": {
              background: "#2F36CB !important",
              fill: "#2F36CB !important",
              stroke: "#2F36CB !important",
            },
            "& .MuiStepIcon-text": {
              display: "none",
            },
            "& .MuiStepLabel-label": {
              color: "#2F36CB !important",
              fontSize: "11px !important",
              fontWeight: "500 !important",
              opacity: "0.6",
            },
            "& .MuiStepLabel-label.Mui-active": {
              color: "#2F36CB !important",
              opacity: "1",
              fontWeight: "600 !important",
            },
            "& .MuiStepLabel-label.Mui-completed": {
              color: "#2F36CB !important",
              opacity: "0.8",
            },
          }}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </MDBox>

      {/* Body */}
      <MDBox sx={{ ...modalStyles.body, maxHeight: 420, overflow: "auto" }}>
        {getStepContent()}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2, fontSize: 12 }}>{errorMessage}</Alert>
        )}
      </MDBox>

      {/* Footer */}
      <MDBox sx={modalStyles.footer}>
        {activeStep > 0 && activeStep < 6 && (
          <Box component="button" sx={modalStyles.secondaryButton} onClick={handleBack}>
            Back
          </Box>
        )}
        {activeStep < 6 ? (
          <Box component="button" sx={modalStyles.primaryButton} onClick={handleNext}>
            {activeStep === 5 ? "Create Tenant" : "Next"}
          </Box>
        ) : (
          <Box component="button" sx={modalStyles.primaryButton} onClick={handleClose}>
            {defaultUserMessage?.hzStatus === "200" ? "Done" : "Close"}
          </Box>
        )}
      </MDBox>
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  usersData: state.CreateUserDetailsReducer.usersData,
  message: state.createDatabaseReducer.message,
  defaultUserMessage: state.createDatabaseReducer.defaultUserMessage,
});

const mapDispatchToProps = (dispatch) => ({
  createUserDetailsAction: (usersDetails) => dispatch(createUserDetailsAction(usersDetails)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddTenantModal);
